import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { FileSearch, ShieldCheck, X } from "lucide-react";
import SourceEvidenceLink, { amendmentEvidence } from "./SourceEvidenceLink";

export function amendmentPdfEndpoint({ hsk, seqNo } = {}) {
  const digits = String(hsk || "").replace(/[^0-9]/g, "");
  const sequence = Number(seqNo);
  if (!/^\d{10}$/.test(digits) || !Number.isInteger(sequence) || sequence < 1) return null;
  return `/api/onto/amendment-evidence/pdf?hsk=${digits}&seqNo=${sequence}`;
}

export function amendmentPdfViewerUrl({ hsk, item } = {}) {
  if (item?.document) {
    return `/pdfjs-viewer/viewer.html?file=${encodeURIComponent(item.document.url)}#page=${item.document.startPage}&zoom=page-width&scrollMode=0&spreadMode=0&pagemode=none`;
  }
  const endpoint = amendmentPdfEndpoint({ hsk, seqNo: item?.seqNo });
  if (!endpoint) return null;
  return `/pdfjs-viewer/viewer.html?file=${encodeURIComponent(endpoint)}#page=1&zoom=page-width&scrollMode=0&spreadMode=0&pagemode=none`;
}

export function AmendmentPdfViewerButton({ hsk, item, onOpen }) {
  const viewerUrl = amendmentPdfViewerUrl({ hsk, item });
  if (!viewerUrl) return null;
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onOpen?.(item);
      }}
      title={`변경고시 연번 ${item?.seqNo}의 공식 원본을 LoginHS에서 보기`}
      aria-label={`변경고시 연번 ${item?.seqNo} 원본 보기`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        minHeight: 24,
        padding: "2px 7px",
        border: "1px solid var(--audit-accent, #2563eb)",
        borderRadius: 5,
        background: "var(--audit-accent-faint, rgba(37,99,235,0.08))",
        color: "var(--audit-accent, #2563eb)",
        fontFamily: "inherit",
        fontSize: "var(--audit-type-caption-size, 12px)",
        fontWeight: 700,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        cursor: "pointer",
      }}
    >
      <FileSearch size={13} aria-hidden="true" />
      원본 보기
    </button>
  );
}

export default function AmendmentPdfViewerModal({ hsk, item, onClose }) {
  const viewerUrl = useMemo(() => amendmentPdfViewerUrl({ hsk, item }), [hsk, item]);
  const evidence = useMemo(() => amendmentEvidence(item), [item]);

  useEffect(() => {
    const keydown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", keydown);
    };
  }, [onClose]);

  if (!viewerUrl) return null;
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`품목분류 변경고시 연번 ${item?.seqNo || "—"} 원본`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 5000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
        background: "rgba(15, 23, 42, 0.58)",
      }}
    >
      <div
        className="audit-root"
        style={{
          width: "min(1180px, calc(100vw - 36px))",
          height: "min(900px, calc(100vh - 36px))",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid var(--audit-border-strong, #cbd5e1)",
          borderRadius: 10,
          background: "var(--audit-bg-elevated, #fff)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
        }}
      >
        <div
          style={{
            minHeight: 54,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "9px 12px 9px 16px",
            flexWrap: "wrap",
            borderBottom: "1px solid var(--audit-border, #e5e7eb)",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 750, color: "var(--audit-text, #111827)" }}>
                품목분류 변경고시 원본 · 연번 {item?.seqNo || "—"}{item?.document ? ` · ${item.document.startPage}–${item.document.endPage}쪽` : " · 1건 단위"}
              </div>
              <span
                title="국가법령정보센터 공식 PDF와 SHA-256이 일치하는 원본"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  flexShrink: 0,
                  padding: "2px 6px",
                  borderRadius: 999,
                  background: "#ecfdf5",
                  color: "#047857",
                  fontSize: 11.5,
                  fontWeight: 750,
                  lineHeight: 1.35,
                }}
              >
                <ShieldCheck size={12} aria-hidden="true" />
                공식 원문 검증
              </span>
            </div>
            <div
              style={{
                marginTop: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: 12,
                color: "var(--audit-text-muted, #64748b)",
              }}
            >
              {item?.gosiNo ? `고시 ${item.gosiNo}` : "현행 변경고시"}
              {item?.title ? ` · ${item.title}` : ""}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7, flexShrink: 0, flexWrap: "wrap", maxWidth: "100%" }}>
            <SourceEvidenceLink compact {...evidence.landing} label={item?.document ? "국가법령정보센터에서 보기" : "공식 별표·서식"} />
            {item?.document && <a href={item.document.url} download={`품목분류변경고시-공식원본.pdf`} style={{ fontSize: 12, whiteSpace: "nowrap" }}>원본 다운로드</a>}
            <a
              href={viewerUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                minHeight: 30,
                padding: "3px 8px",
                border: "1px solid var(--audit-border-strong, #cbd5e1)",
                borderRadius: 6,
                background: "var(--audit-bg, #fff)",
                color: "var(--audit-text, #111827)",
                fontSize: 12,
                fontWeight: 700,
                textDecoration: "none",
                whiteSpace: "nowrap",
              }}
            >
              {item?.document ? "전체 문서 새 창" : "새 창에서 보기"}
            </a>
            <button
              type="button"
              onClick={onClose}
              aria-label="변경고시 원본 닫기"
              title="닫기 (Esc)"
              style={{
                width: 32,
                height: 32,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid var(--audit-border-strong, #cbd5e1)",
                borderRadius: 6,
                background: "var(--audit-bg, #fff)",
                color: "var(--audit-text, #111827)",
                cursor: "pointer",
              }}
            >
              <X size={17} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            background: "#d9dde3",
          }}
        >
          <iframe
            src={viewerUrl}
            title={`품목분류 변경고시 연번 ${item?.seqNo || "—"} 공식 PDF`}
            loading="eager"
            allowFullScreen
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              border: 0,
              background: "#d9dde3",
            }}
          />
        </div>

        <div
          style={{
            minHeight: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "6px 14px",
            borderTop: "1px solid var(--audit-border, #e5e7eb)",
            fontSize: 11.5,
            color: "var(--audit-text-muted, #64748b)",
          }}
        >
          <span>{item?.document ? "국가법령정보센터 공식 PDF 원본 그대로입니다. 해당 항목으로 이동했으며 앞뒤 페이지도 볼 수 있습니다." : "★ 국가법령정보센터 공식 PDF를 SHA-256으로 검증한 뒤 해당 고시만 표시합니다."}</span>
          <strong style={{ flexShrink: 0, color: "var(--audit-text, #111827)", fontWeight: 750 }}>
            {item?.document ? `전체 ${item.document.pageCount}쪽 · PDF 파일 쪽수 기준` : `연번 ${item?.seqNo || "—"} · 단일 페이지 보기`}
          </strong>
        </div>
      </div>
    </div>,
    document.body,
  );
}
